import { useState, useEffect, useRef } from "react";
import { getSpaceshipActionCost, getAgencyActionCost, SpaceshipAction, AgencyAction, ACTION_STATUSES, ActionStatus } from "../shared/constants";
import { buildPlayerName, sortByTimestampDescending } from "../shared/helper";
import { authenticatedFetch, API_BASE_URL } from "../utils/api";
import type { AdminAction as Action } from "../types/admin";
import AdminMessageModal from "./AdminMessageModal";
import ActionReviewModal from "./ActionReviewModal";

interface ViewGameActionsProps {
  gameId: string;
  onClose: () => void;
  refreshKey?: number;
  actionsData?: Action[];
}

export default function ViewGameActions({ gameId, onClose, refreshKey, actionsData }: ViewGameActionsProps) {
  const getStatusColors = (status: string) => {
    switch (status) {
      case ActionStatus.COMPLETED:
        return { backgroundColor: "#d4edda", color: "#155724" };
      case ActionStatus.CANCELED:
        return { backgroundColor: "#f8d7da", color: "#721c24" };
      case ActionStatus.ADMIN:
        return { backgroundColor: "#e3f2fd", color: "#1976d2" };  // Same blue-ish color as admin messages
      case ActionStatus.PENDING:
        return { backgroundColor: "#fff3cd", color: "#856404" };  // Yellowish for pending
      default:
        return { backgroundColor: "#fff3cd", color: "#856404" };  // Default to yellowish
    }
  };
  const [actions, setActions] = useState<Action[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<Set<string>>(new Set());
  const seenActionIdsRef = useRef<Set<string>>(new Set());
  const previousActionsDataRef = useRef<Action[]>([]);
  const [messageModalState, setMessageModalState] = useState<{
    isOpen: boolean;
    playerId: string;
    playerName: string;
    teamName: string;
  }>({
    isOpen: false,
    playerId: "",
    playerName: "",
    teamName: ""
  });
  const [reviewModalState, setReviewModalState] = useState<{
    isOpen: boolean;
    action: Action | null;
    previousStatus: string | null;
    spaceshipCurrentLocation: string | null;
    spaceshipDamage: number | null;
    spaceshipShieldsOn: boolean | null;
  }>({
    isOpen: false,
    action: null,
    previousStatus: null,
    spaceshipCurrentLocation: null,
    spaceshipDamage: null,
    spaceshipShieldsOn: null
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState<boolean>(false);
  const actionsRef = useRef<Action[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  useEffect(() => {
    if (actionsData) {
      // Check for new actions that weren't in the current actions state
      const currentActionIds = new Set(actionsRef.current.map(a => a.action_id));
      const newActions = actionsData.filter(a => !currentActionIds.has(a.action_id));
      
      // If there are new actions and we have existing actions displayed, add new ones to the top
      if (newActions.length > 0 && actionsRef.current.length > 0) {
        // Get existing actions (excluding the new ones) and update any that changed
        const existingActions = actionsRef.current
          .filter(a => actionsData.some(newAction => newAction.action_id === a.action_id))
          .map(existingAction => {
            const updated = actionsData.find(a => a.action_id === existingAction.action_id);
            return updated || existingAction;
          });
        
        // Sort existing actions by timestamp
        const sortedExisting = sortByTimestampDescending(existingActions, action => action.timestamp);
        
        // New actions go to the top, then existing actions
        setActions([...newActions, ...sortedExisting]);
      } else {
        // Initial load or no new actions - just sort all by timestamp
        const sorted = sortByTimestampDescending(actionsData, action => action.timestamp);
        setActions(sorted);
      }
      
      // Update refs
      previousActionsDataRef.current = actionsData;
      actionsData.forEach(a => seenActionIdsRef.current.add(a.action_id));
      
      setIsLoading(false);
      setError("");
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError("");
    fetch(`${API_BASE_URL}/games/actions?game_id=${gameId}`)
      .then(res => {
        if (!res.ok) {
          if (res.status === 404) {
            return [];
          }
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to fetch actions");
          });
        }
        return res.json();
      })
      .then((data: Action[]) => {
        if (cancelled) return;
        const sorted = sortByTimestampDescending(data, action => action.timestamp);
        setActions(sorted);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to fetch actions");
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actionsData, gameId, refreshKey]);

  // Build aligned team → players map to render agencies on one row and spaceships on another
  const buildTeamPlayers = () => {
    const teamOrder: string[] = [];
    const teamToPlayers: Record<string, {
      agency?: { id: string; name: string };
      spaceship?: { id: string; name: string };
    }> = {};

    actions.forEach(action => {
      const teamName = action.team_name;
      if (!teamName) return;
      
      if (!teamToPlayers[teamName]) {
        teamToPlayers[teamName] = {};
        teamOrder.push(teamName);
      }
      
      const roleValue = action.player_role ?? "";
      const roleLower = roleValue.toLowerCase();
      const playerName = buildPlayerName(action.team_name, roleValue);
      if (roleLower.includes("agency")) {
        teamToPlayers[teamName].agency = { id: action.player_id, name: playerName };
      } else if (roleLower.includes("spaceship")) {
        teamToPlayers[teamName].spaceship = { id: action.player_id, name: playerName };
      }
    });

    return { teamOrder, teamToPlayers };
  };

  // Filter actions based on selected player
  const filteredActions = selectedPlayerId
    ? actions.filter(action => action.player_id === selectedPlayerId)
    : actions;

  const { teamOrder, teamToPlayers } = buildTeamPlayers();

  // Calculate cost for an action
  const getActionCost = (actionType: string): number => {
    try {
      if (Object.values(SpaceshipAction).includes(actionType as SpaceshipAction)) {
        return getSpaceshipActionCost(actionType as SpaceshipAction);
      } else if (Object.values(AgencyAction).includes(actionType as AgencyAction)) {
        return getAgencyActionCost(actionType as AgencyAction);
      }
    } catch (e) {
      // If action doesn't match, cost remains 0
    }
    return 0;
  };

  const handleStatusChange = async (actionId: string, newStatus: string) => {
    const action = actions.find(a => a.action_id === actionId);
    if (!action) return;

    const previousStatus = action.status;

    // If status is actually changing, open review modal
    if (previousStatus !== newStatus) {
      // For spaceship actions (move or scan) or agency scan actions, fetch the spaceship location
      let spaceshipCurrentLocation: string | null = null;
      let spaceshipDamage: number | null = null;
      let spaceshipShieldsOn: boolean | null = null;
      const isSpaceshipAction = action.player_role && action.player_role.toLowerCase().includes("spaceship");
      const isAgencyAction = action.player_role && action.player_role.toLowerCase().includes("agency");
      const isSpaceshipMove = (action.action === "Move" || action.action === "MOVE") && isSpaceshipAction;
      const isSpaceshipScan = (action.action === "Scan" || action.action === "SCAN") && isSpaceshipAction;
      const isAgencyScan = (action.action === "Scan" || action.action === "SCAN") && isAgencyAction;
      
      if (isSpaceshipMove || isSpaceshipScan || isAgencyScan) {
        try {
          // Get player data to find current_location, damage, and shields using full-data endpoint
          const fullDataResponse = await authenticatedFetch(`/games/full-data?game_id=${gameId}`);
          if (fullDataResponse.ok) {
            const fullData = await fullDataResponse.json();
            const players = fullData.players || [];
            
            let spaceshipPlayer: any = null;
            if (isSpaceshipAction) {
              // For spaceship actions, find the spaceship player directly
              spaceshipPlayer = players.find((p: any) => p.player_id === action.player_id);
            } else if (isAgencyScan) {
              // For agency scan actions, find the spaceship player on the same team
              const agencyPlayer = players.find((p: any) => p.player_id === action.player_id);
              if (agencyPlayer && agencyPlayer.team_id) {
                spaceshipPlayer = players.find((p: any) => 
                  p.team_id === agencyPlayer.team_id && 
                  p.role && p.role.toLowerCase().includes("spaceship")
                );
              }
            }
            
            if (spaceshipPlayer) {
              spaceshipCurrentLocation = spaceshipPlayer.current_location || null;
              // Only fetch damage and shields for move actions
              if (isSpaceshipMove) {
                spaceshipDamage = typeof spaceshipPlayer.ship_dmg === "number" ? spaceshipPlayer.ship_dmg : 0;
                spaceshipShieldsOn = typeof spaceshipPlayer.shields_on === "boolean" ? spaceshipPlayer.shields_on : false;
              }
            }
          }
        } catch (err) {
          console.error("Error fetching spaceship data:", err);
          // Continue without data if fetch fails
        }
      }

      setReviewModalState({
        isOpen: true,
        action: { ...action, status: newStatus }, // Store the new status in the action
        previousStatus: previousStatus,
        spaceshipCurrentLocation: spaceshipCurrentLocation,
        spaceshipDamage: spaceshipDamage,
        spaceshipShieldsOn: spaceshipShieldsOn
      });
      return; // Don't update status yet, wait for modal confirmation
    }

    // If status hasn't changed, no need to do anything
  };

  const updateActionStatus = async (actionId: string, newStatus: string) => {
    setUpdatingStatus(prev => new Set(prev).add(actionId));
    try {
      const res = await authenticatedFetch("/players/action/status", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action_id: actionId, status: newStatus })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to update action status");
      }

      setActions(prevActions =>
        prevActions.map(action =>
          action.action_id === actionId ? { ...action, status: newStatus } : action
        )
      );

      if (newStatus === ActionStatus.CANCELED) {
        alert("You changed an action's status to CANCELED. don't forget to MANUALLY REVERSE all of its effects");
      }
    } catch (err: any) {
      console.error("Error updating action status:", err);
      setError(err.message || "Failed to update action status");
    } finally {
      setUpdatingStatus(prev => {
        const next = new Set(prev);
        next.delete(actionId);
        return next;
      });
    }
  };

  const handleReviewSave = async (scanResult: string) => {
    if (!reviewModalState.action) return;

    const actionId = reviewModalState.action.action_id;
    const playerId = reviewModalState.action.player_id;
    const newStatus = reviewModalState.action.status; // Use the status that was selected
    
    // Check if this is a spaceship scan action
    const isSpaceshipScan = (reviewModalState.action.action === "Scan" || reviewModalState.action.action === "SCAN") && 
      reviewModalState.action.player_role && reviewModalState.action.player_role.toLowerCase().includes("spaceship");

    // Check if this is an agency scan action
    const isAgencyScan = (reviewModalState.action.action === "Scan" || reviewModalState.action.action === "SCAN") && 
      reviewModalState.action.player_role && reviewModalState.action.player_role.toLowerCase().includes("agency");

    // Check if this is a spaceship move action
    const isSpaceshipMove = (reviewModalState.action.action === "Move" || reviewModalState.action.action === "MOVE") && 
      reviewModalState.action.player_role && reviewModalState.action.player_role.toLowerCase().includes("spaceship");

    setIsSubmittingReview(true);
    try {
      // For spaceship or agency scans, send the scan result as a message to the player
      if ((isSpaceshipScan || isAgencyScan) && scanResult.trim()) {
        // Send just the scan result text
        const message = scanResult.trim();
        
        const messageResponse = await authenticatedFetch("/messages/admin-send", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            player_id: playerId,
            message: message,
          }),
        });

        if (!messageResponse.ok) {
          const errorData = await messageResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to send message to player");
        }
      }

      // For spaceship moves, send the move results to both spaceship and agency
      if (isSpaceshipMove && scanResult.trim()) {
        // Parse the move data (JSON string with path, newCurrentLocation, damage, and shieldsOn)
        let moveData: { path: string; newCurrentLocation: string; damage?: number; shieldsOn?: boolean };
        try {
          moveData = JSON.parse(scanResult);
        } catch (e) {
          throw new Error("Invalid move data format");
        }

        const { path, newCurrentLocation, damage, shieldsOn } = moveData;
        // Send just the path text
        const message = path.trim();

        // Get team name from action
        const teamName = reviewModalState.action.team_name;
        if (!teamName) {
          throw new Error("Team name not found for this action");
        }

        // Get all players for the game to find the agency player using full-data endpoint
        const fullDataResponse = await authenticatedFetch(`/games/full-data?game_id=${gameId}`);
        if (!fullDataResponse.ok) {
          throw new Error("Failed to fetch players");
        }
        const fullData = await fullDataResponse.json();
        const players = fullData.players || [];
        
        // Find the agency player for the same team (match by team_name)
        const agencyPlayer = players.find((p: any) => 
          p.team_name === teamName && 
          p.role && p.role.toLowerCase().includes("agency")
        );

        if (!agencyPlayer) {
          throw new Error("Agency player not found for this team");
        }

        // Send message to spaceship player
        const spaceshipMessageResponse = await authenticatedFetch("/messages/admin-send", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            player_id: playerId,
            message: message,
          }),
        });

        if (!spaceshipMessageResponse.ok) {
          const errorData = await spaceshipMessageResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to send message to spaceship player");
        }

        // Send message to agency player
        const agencyMessageResponse = await authenticatedFetch("/messages/admin-send", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            player_id: agencyPlayer.player_id,
            message: message,
          }),
        });

        if (!agencyMessageResponse.ok) {
          const errorData = await agencyMessageResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to send message to agency player");
        }

        // Update the spaceship's current_location in the database
        const locationUpdateResponse = await authenticatedFetch("/players/location/update", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            player_id: playerId,
            game_id: gameId,
            agency_player_id: agencyPlayer.player_id,
            current_location: newCurrentLocation,
          }),
        });

        if (!locationUpdateResponse.ok) {
          const errorData = await locationUpdateResponse.json().catch(() => ({}));
          console.error("Failed to update spaceship location:", errorData);
          // Don't throw error here - message was sent successfully, location update is secondary
        }

        // Update damage if it changed
        if (damage !== undefined && damage !== null) {
          const currentDamage = reviewModalState.spaceshipDamage ?? 0;
          if (damage !== currentDamage) {
            const damageDelta = damage - currentDamage;
            const damageEndpoint = damageDelta > 0 ? "/players/damage/inc" : "/players/damage/dec";
            const steps = Math.abs(damageDelta);
            for (let i = 0; i < steps; i++) {
              const damageResponse = await authenticatedFetch(damageEndpoint, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  team_name: reviewModalState.action.team_name,
                  player_role: reviewModalState.action.player_role
                }),
              });
              if (!damageResponse.ok) {
                const errorData = await damageResponse.json().catch(() => ({}));
                console.error("Failed to update damage:", errorData);
                // Continue even if damage update fails
              }
            }
          }
        }

        // Update shields if they changed
        if (shieldsOn !== undefined && shieldsOn !== null) {
          const currentShields = reviewModalState.spaceshipShieldsOn ?? false;
          if (shieldsOn !== currentShields) {
            const shieldsEndpoint = shieldsOn ? "/players/shields/on" : "/players/shields/off";
            const shieldsResponse = await authenticatedFetch(shieldsEndpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                player_id: playerId
              }),
            });
            if (!shieldsResponse.ok) {
              const errorData = await shieldsResponse.json().catch(() => ({}));
              console.error("Failed to update shields:", errorData);
              // Continue even if shields update fails
            }
          }
        }
      }

      // Close modal after message is sent (if applicable) or confirmation
      setReviewModalState({
        isOpen: false,
        action: null,
        previousStatus: null,
        spaceshipCurrentLocation: null,
        spaceshipDamage: null,
        spaceshipShieldsOn: null
      });

      // Update the action status
      await updateActionStatus(actionId, newStatus);
    } catch (err: any) {
      console.error("Error in action review:", err);
      setError(err.message || "Failed to complete action review");
      // Don't close the modal if there was an error
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleReviewCancel = () => {
    // Reset the action status back to PENDING if modal is cancelled
    if (reviewModalState.action && reviewModalState.previousStatus) {
      // Reset the dropdown to previous status
      const actionId = reviewModalState.action.action_id;
      setActions(prevActions =>
        prevActions.map(action =>
          action.action_id === actionId ? { ...action, status: reviewModalState.previousStatus! } : action
        )
      );
    }
    setReviewModalState({
      isOpen: false,
      action: null,
      previousStatus: null,
      spaceshipCurrentLocation: null,
      spaceshipDamage: null,
      spaceshipShieldsOn: null
    });
    setIsSubmittingReview(false);
  };

  return (
    <div>
      {!isLoading && !error && actions.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={() => setSelectedPlayerId(null)}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: "pointer",
              backgroundColor: selectedPlayerId === null ? "#007bff" : "#e9ecef",
              color: selectedPlayerId === null ? "white" : "#212529",
              border: "none",
              borderRadius: "4px"
            }}
          >
            All Actions
          </button>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            {teamOrder.map((team) => {
              const p = teamToPlayers[team]?.agency;
              if (!p) return null;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlayerId(p.id)}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    backgroundColor: selectedPlayerId === p.id ? "#007bff" : "#e9ecef",
                    color: selectedPlayerId === p.id ? "white" : "#212529",
                    border: "none",
                    borderRadius: "4px"
                  }}
                >
                  {p.name}
                </button>
              );
            })}
            {teamOrder.map((team) => {
              const p = teamToPlayers[team]?.spaceship;
              if (!p) return null;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlayerId(p.id)}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    backgroundColor: selectedPlayerId === p.id ? "#007bff" : "#e9ecef",
                    color: selectedPlayerId === p.id ? "white" : "#212529",
                    border: "none",
                    borderRadius: "4px"
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && (
        <div style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}>Loading actions...</div>
      )}

      {error && (
        <div style={{ padding: "1rem", color: "#dc3545", fontSize: "0.875rem" }}>{error}</div>
      )}

      {!isLoading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.875rem"
          }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Turn</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}></th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Player</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Action</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Cost</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Status</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Collaborators</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    No actions found
                  </td>
                </tr>
              ) : selectedPlayerId && filteredActions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    No actions found for selected player
                  </td>
                </tr>
              ) : (
                filteredActions.map((action) => {
                  const hasCollaborators = Boolean(action.collaborators && action.collaborators.trim() !== "");
                  const playerName = buildPlayerName(action.team_name, action.player_role);
                  
                  // Determine row background color based on status
                  let rowBackgroundColor = "transparent";
                  switch (action.status) {
                    case ActionStatus.ADMIN:
                      rowBackgroundColor = "#e3f2fd";  // Blue-ish background (same as admin messages)
                      break;
                    case ActionStatus.PENDING:
                      rowBackgroundColor = "#fff3cd";  // Yellowish background
                      break;
                    case ActionStatus.CANCELED:
                      rowBackgroundColor = "#f8d7da";  // Light red background
                      break;
                    case ActionStatus.COMPLETED:
                      rowBackgroundColor = "#d4edda";  // Light green background
                      break;
                    default:
                      rowBackgroundColor = hasCollaborators ? "#fff7e6" : "transparent";
                  }
                  
                  return (
                    <tr
                      key={action.action_id}
                      style={{
                        borderBottom: "1px solid #dee2e6",
                        backgroundColor: rowBackgroundColor,
                      }}
                    >
                      <td style={{ padding: "0.75rem", color: "#212529" }}>{action.turn}</td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>
                        {action.player_id && action.team_name ? (
                          <button
                            onClick={() => {
                              setMessageModalState({
                                isOpen: true,
                                playerId: action.player_id!,
                                playerName: playerName,
                                teamName: action.team_name!
                              });
                            }}
                            style={{
                              padding: "0.25rem 0.75rem",
                              fontSize: "0.875rem",
                              cursor: "pointer",
                              backgroundColor: "#007bff",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              fontWeight: "500"
                            }}
                            title="Send Message"
                          >
                            Send Message
                          </button>
                        ) : (
                          <span style={{ color: "#6c757d" }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>
                        {playerName}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>{action.action}</td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>{getActionCost(action.action)}</td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>
                        <select
                          value={action.status}
                          onChange={(e) => handleStatusChange(action.action_id, e.target.value)}
                          disabled={updatingStatus.has(action.action_id)}
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: getStatusColors(action.status).backgroundColor,
                            color: getStatusColors(action.status).color,
                            borderRadius: "4px",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            border: "1px solid #dee2e6",
                            cursor: updatingStatus.has(action.action_id) ? "not-allowed" : "pointer",
                            opacity: updatingStatus.has(action.action_id) ? 0.6 : 1
                          }}
                        >
                          {ACTION_STATUSES.map(status => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "200px", wordWrap: "break-word" }}>
                        {action.collaborators || "—"}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "300px", wordWrap: "break-word" }}>
                        <span style={{ whiteSpace: "pre-line" }}>{action.details || "-"}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <AdminMessageModal
        playerId={messageModalState.playerId}
        playerName={messageModalState.playerName}
        teamName={messageModalState.teamName}
        isOpen={messageModalState.isOpen}
        onClose={() => {
          setMessageModalState({
            isOpen: false,
            playerId: "",
            playerName: "",
            teamName: ""
          });
        }}
        onSuccess={() => {
          // Message sent successfully - modal will close automatically
        }}
      />

      <ActionReviewModal
        isOpen={reviewModalState.isOpen}
        onClose={handleReviewCancel}
        action={reviewModalState.action}
        onSave={handleReviewSave}
        isSubmitting={isSubmittingReview}
        spaceshipCurrentLocation={reviewModalState.spaceshipCurrentLocation}
        spaceshipDamage={reviewModalState.spaceshipDamage}
        spaceshipShieldsOn={reviewModalState.spaceshipShieldsOn}
      />
    </div>
  );
}

